import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter from '../../versa/filter.js';
import router from '../../versa/router.js';
import { notify } from '../../versa/msgbox.js';
import view from '../views/inventory-sessions.js';
import form from '../forms/inventory-sessions.js';
import * as types from '../types.js';

const inventorySessions = function()
{
	const inv_crud = crud.create({

		_lastRows: null,
		_lastFilterKey: null,

		async loadBaseData() {
			try {
				const data = await api.call(api.list_companies, this.queryAll());
				await db.set('companies', data);
			} catch (err) {
				notify(err);
			}
		},

		async editFilter() {
			filter.edit(this.applyFilter.bind(this), this.filter);
		},

		async applyFilter(data) {
			this.filter = data;
			this.filterExplicitlyApplied = true;
			this._invalidateRowsCache();
			const companyId = this._resolveCompanyIdFromFilter();
			if (companyId)
				this._persistCompanyFilter(companyId);
			await this.loadPage(1);
		},

		_invalidateRowsCache() {
			this._lastRows = null;
			this._lastFilterKey = null;
		},

		/** Atualizar: limpa cache local e busca de novo na API. */
		reloadPage() {
			this._invalidateRowsCache();
			this.loadPage(this.page_number > 0 ? this.page_number : 1);
		},

		_persistCompanyFilter(companyId) {
			try {
				localStorage.setItem('inventory-sessions.company_id', String(companyId));
			} catch (_) { /* quota / modo privado */ }
		},

		_setCompanyFilter(companyId) {
			if (!companyId) return;
			this.filter = {
				...(this.filter || {}),
				'field-0': 'company_id',
				'operator-0': 'eq',
				'value-0': String(companyId),
			};
			this.filterExplicitlyApplied = true;
			this._persistCompanyFilter(companyId);
			this._invalidateRowsCache();
		},

		_resolveCompanyIdFromFilter() {
			for (let i = 0; i < 10; i++) {
				if (this.filter?.[`field-${i}`] === 'company_id') {
					const v = this.filter?.[`value-${i}`];
					return v && String(v).trim() ? String(v).trim() : null;
				}
			}
			return null;
		},

		/**
		 * Aplica critérios do Filtrar na lista já carregada (API não recebe filter Versa).
		 * @param {object[]} rows
		 * @returns {object[]}
		 */
		_applyClientFilter(rows) {
			const f = this.filter || {};
			const clauses = [];
			for (let i = 0; i < 10; i++) {
				const field = f[`field-${i}`];
				const value = f[`value-${i}`];
				if (!field || value == null || String(value).trim() === '')
					continue;
				clauses.push({
					field,
					op: String(f[`operator-${i}`] || 'eq').toLowerCase(),
					value: String(value).trim(),
				});
			}
			if (!clauses.length)
				return rows;

			return rows.filter((row) => clauses.every(({ field, op, value }) => {
				let raw = row[field];
				if (field === 'company_id' && raw == null)
					raw = row.company_id;
				if (field === 'status') {
					const n = Number(raw);
					const v = Number(value);
					if (op === 'ne') return n !== v;
					if (op === 'ge') return n >= v;
					if (op === 'le') return n <= v;
					return n === v;
				}
				if (field === 'id') {
					const n = Number(raw);
					const v = Number(value);
					if (op === 'ne') return n !== v;
					if (op === 'ge') return n >= v;
					if (op === 'le') return n <= v;
					return n === v;
				}
				if (field === 'created_on') {
					const left = String(raw || '').substring(0, 10);
					const right = String(value).substring(0, 10);
					if (op === 'ge') return left >= right;
					if (op === 'le') return left <= right;
					if (op === 'ne') return left !== right;
					return left === right;
				}
				const left = String(raw ?? '').toLowerCase();
				const right = value.toLowerCase();
				if (op === 'co' || op === 'contains')
					return left.includes(right);
				if (op === 'ne')
					return left !== right;
				return left === right;
			}));
		},

		async _ensureRows() {
			const companyId = this._resolveCompanyIdFromFilter();
			const key = companyId ? `company:${companyId}` : 'all-companies';
			if (this._lastRows && this._lastFilterKey === key)
				return this._lastRows;

			const res = await api.list_inventory_sessions(companyId || undefined);
			if (!res.ok)
				throw new Error(await _apiErrorText(res));
			const rows = await res.json();
			this._lastRows = Array.isArray(rows) ? rows : [];
			this._lastFilterKey = key;
			return this._lastRows;
		},

		async queryData(query) {
			const all = await this._ensureRows();
			const rows = this._applyClientFilter(all);
			const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
			if (qs.get('group') === 'count')
				return rows.length;
			const limit = Number(qs.get('limit') || 30);
			const offset = Number(qs.get('offset') || 0);
			return rows.slice(offset, offset + limit);
		},

		async loadRecord(id_json) {
			const [companyId, sessionId] = JSON.parse(id_json);
			const res = await api.select_inventory_session(companyId, sessionId);
			if (!res.ok)
				throw new Error(await _apiErrorText(res));
			return await res.json();
		},

		async saveRecord(data) {
			if (data.id)
				return data.id; // edição é feita por ações no detalhe

			const companyId = data.company_id;
			if (!companyId)
				throw new Error('Empresa obrigatória.');
			if (!data.name || !String(data.name).trim())
				throw new Error('Nome da sessão obrigatório.');

			const payload = {
				company_id: companyId,
				session_type: data.session_type || 'position',
				name: String(data.name).trim(),
				notes: data.notes || null,
				scope_filter: _buildScopeFilter(data),
				freeze_immediately: data.session_type !== 'import',
			};

			const res = await api.create_inventory_session(payload);
			if (!res.ok)
				throw new Error(await _apiErrorText(res));
			const created = await res.json();
			this._setCompanyFilter(companyId);
			notify('Sessão de inventário criada com sucesso!', 'success');
			return created.id;
		},

		async saveRecordWrapper(data, oldData) {
			try {
				return await Object.getPrototypeOf(this).saveRecordWrapper.call(this, data, oldData);
			} catch (err) {
				notify(err?.message || String(err), 'Inventário');
				return false;
			}
		},

		setMenuRoutes() {
			Object.getPrototypeOf(this).setMenuRoutes.call(this);
			router.set('#inventory-view-counts', () => this._openSelectedInventoryDetail());
			router.set('#inventory-print-progress', () => this._printSelectedInventoryProgress());
			router.set('#inventory-print-divergences', () => this._printSelectedInventoryDivergences());
			router.set('#inventory-print-simulation-sku', () => this._printSelectedInventorySimulationBySku());
			router.set('#inventory-export-simulation-sku', () => this._exportSelectedInventorySimulationBySku());
			router.set('#inventory-export-closure-sku', () => this._exportSelectedInventoryClosureBySku());
			router.set('#inventory-freeze', () => this._runSelectedInventoryAction({
				label: 'congelar',
				confirmMessage: 'Congelar inventário(s) selecionado(s)?',
				call: (companyId, sessionId, row) => api.freeze_inventory_session(companyId, sessionId, row.scope_filter || null),
				successMessage: 'Inventário(s) congelado(s).',
			}));
			router.set('#inventory-unfreeze', () => this._runSelectedInventoryAction({
				label: 'descongelar',
				confirmMessage: 'Descongelar inventário(s) selecionado(s)? Os itens congelados serão removidos.',
				call: (companyId, sessionId) => api.unfreeze_inventory_session(companyId, sessionId),
				successMessage: 'Inventário(s) descongelado(s).',
			}));
			router.set('#inventory-cancel', () => this._runSelectedInventoryAction({
				label: 'cancelar',
				confirmMessage: 'Cancelar inventário(s) selecionado(s) com status Criado?',
				call: (companyId, sessionId) => api.cancel_inventory_session(companyId, sessionId),
				successMessage: 'Inventário(s) cancelado(s).',
			}));
		},

		async deleteRecord() {
			return await this._runSelectedInventoryAction({
				label: 'cancelar',
				confirmMessage: 'Cancelar inventário(s) selecionado(s) com status Criado?',
				call: (companyId, sessionId) => api.cancel_inventory_session(companyId, sessionId),
				successMessage: 'Inventário(s) cancelado(s).',
			});
		},

		/**
		 * Executa uma ação operacional nas sessões selecionadas da grade.
		 * @param {{ label: string, confirmMessage: string, call: Function, successMessage: string }} options
		 * @returns {Promise<boolean>}
		 */
		async _runSelectedInventoryAction({ label, confirmMessage, call, successMessage }) {
			const selected = this.getSelection();
			if (!selected.length) {
				notify('Selecione ao menos um inventário na grade.');
				return false;
			}
			if (!confirm(confirmMessage))
				return false;

			const errors = [];
			for (const id of selected) {
				const [companyId, sessionId] = id;
				try {
					const row = (this._lastRows || []).find(r => String(r.company_id) === String(companyId) && Number(r.id) === Number(sessionId));
					const res = await call(companyId, sessionId, row || {});
					if (!res.ok)
						throw new Error(await _apiErrorText(res));
				} catch (err) {
					errors.push(`#${sessionId}: ${err?.message || err}`);
				}
			}

			this._invalidateRowsCache();
			await this.loadPage();
			if (errors.length)
				notify(`Falha ao ${label}: ${errors.join(' | ')}`);
			else
				notify(successMessage, 'success');
			return !errors.length;
		},

		/**
		 * Abre o detalhe operacional da sessão selecionada para visualizar itens e histórico de contagens.
		 * @returns {Promise<boolean>}
		 */
		async _openSelectedInventoryDetail() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um inventário para ver as contagens.');
				return false;
			}
			try {
				const record = await this.loadRecord(JSON.stringify(selected[0]));
				if (record != null)
					this.editRecord(record);
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},

		/**
		 * Gera o PDF de acompanhamento com os itens e o histórico de contagens da sessão selecionada.
		 * @returns {Promise<boolean>}
		 */
		async _printSelectedInventoryProgress() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um inventário para imprimir o acompanhamento.');
				return false;
			}
			const [companyId, sessionId] = selected[0];
			try {
				const res = await api.print_inventory_progress(companyId, sessionId);
				if (!res.ok)
					throw new Error(await _apiErrorText(res));
				const data = await res.json();
				_downloadBase64(data.content_base64, `acompanhamento-inv-${sessionId}.pdf`, 'application/pdf');
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},

		/**
		 * PDF estruturado de divergências da sessão selecionada.
		 * @returns {Promise<boolean>}
		 */
		async _printSelectedInventoryDivergences() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um inventário para imprimir divergências.');
				return false;
			}
			const [companyId, sessionId] = selected[0];
			try {
				const res = await api.print_inventory_divergences(companyId, sessionId);
				if (!res.ok)
					throw new Error(await _apiErrorText(res));
				const data = await res.json();
				_downloadBase64(data.content_base64, `divergencias-inv-${sessionId}.pdf`, 'application/pdf');
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},

		/**
		 * PDF simulação agregada por SKU (requer simulate-close).
		 * @returns {Promise<boolean>}
		 */
		async _printSelectedInventorySimulationBySku() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um inventário para imprimir a simulação por SKU.');
				return false;
			}
			const [companyId, sessionId] = selected[0];
			try {
				const res = await api.print_inventory_simulation_by_sku(companyId, sessionId);
				if (!res.ok)
					throw new Error(await _apiErrorText(res));
				const data = await res.json();
				_downloadBase64(
					data.content_base64,
					data.filename || `simulacao-sku-inv-${sessionId}.pdf`,
					data.mime_type || 'application/pdf',
				);
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},

		/**
		 * Excel simulação agregada por SKU (requer simulate-close).
		 * @returns {Promise<boolean>}
		 */
		async _exportSelectedInventorySimulationBySku() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um inventário para exportar a simulação por SKU.');
				return false;
			}
			const [companyId, sessionId] = selected[0];
			try {
				const res = await api.export_inventory_simulation_by_sku(companyId, sessionId);
				if (!res.ok)
					throw new Error(await _apiErrorText(res));
				const data = await res.json();
				_downloadBase64(
					data.content_base64,
					data.filename || `simulacao-sku-inv-${sessionId}.xlsx`,
					data.mime_type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				);
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},

		/**
		 * Excel saldo por SKU + programa (itens finalizados do escopo).
		 * @returns {Promise<boolean>}
		 */
		async _exportSelectedInventoryClosureBySku() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um inventário para exportar o saldo por SKU.');
				return false;
			}
			const [companyId, sessionId] = selected[0];
			try {
				const res = await api.export_inventory_closure_by_sku(companyId, sessionId);
				if (!res.ok)
					throw new Error(await _apiErrorText(res));
				const data = await res.json();
				_downloadBase64(
					data.content_base64,
					data.filename || `saldo-sku-inv-${sessionId}.xlsx`,
					data.mime_type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				);
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},
	});

	const fieldspecs = [
		{ index: 1, field: 'company_id',   label: 'Empresa', types: [types.datalist()], store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id',           label: '#',       types: [types.number()], placeholder: '' },
		{ index: 3, field: 'name',         label: 'Nome',    types: [types.name()], placeholder: '' },
		{ index: 4, field: 'session_type', label: 'Tipo',    types: [types.name()], placeholder: '' },
		{ index: 5, field: 'status',       label: 'Status',  types: [types.number()], placeholder: '' },
		{ index: 6, field: 'created_on',   label: 'Criado',  types: [types.date()], placeholder: '' },
	];

	inv_crud.run(
		'inventory-sessions',
		'Inventários V2',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		[
			{ hash: '#inventory-view-counts', label: 'Ver contagens / acompanhamento' },
			{ hash: '#inventory-freeze', label: 'Congelar selecionados' },
			{ hash: '#inventory-unfreeze', label: 'Descongelar selecionados' },
			{ hash: '#inventory-cancel', label: 'Cancelar selecionados' },
		],
		[
			{ hash: '#inventory-print-progress', label: 'Acompanhamento com contagens' },
			{ hash: '#inventory-print-divergences', label: 'Relatório de divergências' },
			{ hash: '#inventory-print-simulation-sku', label: 'Simulação por SKU (PDF)' },
			{ hash: '#inventory-export-simulation-sku', label: 'Simulação por SKU (Excel)' },
			{ hash: '#inventory-export-closure-sku', label: 'Saldo por SKU (Excel)' },
		]
	);
};

/**
 * Quebra campos de escopo em tokens aceitando os separadores usados na operação.
 * @param {string} str
 * @returns {string[]}
 */
function _splitScopeTokens(str) {
	if (!str || !String(str).trim()) return [];
	return String(str)
		.split(/[,\s;]+/)
		.map(s => s.trim())
		.filter(token => token && token.toLowerCase() !== 'e');
}

/**
 * Lê uma lista de IDs aceitando vírgula, ponto e vírgula, espaço ou quebra de linha.
 * @param {string} str
 * @returns {number[]|null}
 */
function _parseIdTokens(str) {
	const nums = _splitScopeTokens(str)
		.map(s => parseInt(s, 10))
		.filter(n => !Number.isNaN(n));
	return nums.length ? nums : null;
}

/**
 * Monta o filtro de congelamento normalizando listas digitadas no formulário.
 * @param {object} data
 * @returns {object|null}
 */
function _buildScopeFilter(data) {
	const parseLocationScope = (str) => {
		if (!str || !String(str).trim()) return { ids: null, codes: null };
		const ids = [];
		const codes = [];
		for (const token of _splitScopeTokens(str)) {
			if (/^\d+$/.test(token))
				ids.push(parseInt(token, 10));
			else
				codes.push(token);
		}
		return {
			ids: ids.length ? ids : null,
			codes: codes.length ? codes : null,
		};
	};

	const f = {};
	const wi = _parseIdTokens(data.scope_warehouse_ids);
	const locScope = parseLocationScope(data.scope_location_ids);
	const productTerms = _splitScopeTokens(data.scope_product_ids);
	const ci = _parseIdTokens(data.scope_category_ids);
	const cli = _parseIdTokens(data.scope_class_ids);
	if (wi) f.warehouse_ids = wi;
	if (locScope.ids) f.location_ids = locScope.ids;
	if (locScope.codes) f.location_codes = locScope.codes;
	if (productTerms.length) f.product_terms = productTerms;
	if (ci) f.category_ids = ci;
	if (cli) f.class_ids = cli;
	return Object.keys(f).length ? f : null;
}

export default inventorySessions;

/**
 * Extrai uma mensagem operacional legível de respostas de erro da API.
 * @param {Response} response
 * @returns {Promise<string>}
 */
async function _apiErrorText(response) {
	const text = await response.text();
	const clean = String(text || '')
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	return clean || `Falha na API ao abrir inventário (HTTP ${response.status}).`;
}

/**
 * Dispara download de um arquivo retornado pela API em base64.
 * @param {string} b64
 * @param {string} filename
 * @param {string} mime
 */
function _downloadBase64(b64, filename, mime) {
	const bin = atob(b64);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++)
		arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mime });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}
